import type {SvgIconComponent} from "@mui/icons-material";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import CampaignOutlinedIcon from "@mui/icons-material/CampaignOutlined";
import EventOutlinedIcon from "@mui/icons-material/EventOutlined";
import HotelOutlinedIcon from "@mui/icons-material/HotelOutlined";
import KeyboardVoiceOutlinedIcon from "@mui/icons-material/KeyboardVoiceOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import RestaurantMenuOutlinedIcon from "@mui/icons-material/RestaurantMenuOutlined";
import SpaOutlinedIcon from "@mui/icons-material/SpaOutlined";
import WidgetsOutlinedIcon from "@mui/icons-material/WidgetsOutlined";
import {Box, Card, Chip, Stack, Typography} from "@mui/material";
import {CommonThemeColors} from "crqlar-react-common/dist/components/theme";
import {AppCategory} from "crqlar-react-common/dist/models/app/app.model";
import {Sector} from "crqlar-react-common/dist/models/sector/sector.model";
import {useAllSectors} from "crqlar-react-common/dist/utils/hooks/sectors/useSectors";
import {useTranslation} from "react-i18next";
import {Link as RouterLink} from "react-router-dom";
import SurveyModuleIcon from "../common/icons/SurveyModuleIcon";
import {useMe} from "../common/useMe";
import type {AvailableApp} from "../common/useMe";
import {buildAppLink} from "../layout/appNavigation";
import {useCrossSaleOpportunities} from "../crossSale/useCrossSaleOpportunities";
import {getSectorIcon} from "crqlar-react-common/dist/components/sectorIcons/sectorIcons";

interface HomeAppIconDefinition {
    sku: string;
    specialization?: string;
    icon: SvgIconComponent;
}

const HOME_APP_ICONS = [
    {sku: "crm", icon: PeopleAltOutlinedIcon},
    {sku: "reservations", specialization: "restaurant", icon: RestaurantMenuOutlinedIcon},
    {sku: "reservations", specialization: "spa", icon: SpaOutlinedIcon},
    {sku: "reservations", specialization: "accommodation", icon: HotelOutlinedIcon},
    {sku: "reservations", specialization: "weekly-planner", icon: CalendarMonthOutlinedIcon},
    {sku: "reservations", icon: EventOutlinedIcon},
    {sku: "booking", icon: WidgetsOutlinedIcon},
    {sku: "marketing", icon: CampaignOutlinedIcon},
    {sku: "surveys", icon: SurveyModuleIcon},
    {sku: "voiceNote", icon: KeyboardVoiceOutlinedIcon},
] satisfies HomeAppIconDefinition[];

const findHomeAppIcon = (sku: string, specialization?: string) => (
    HOME_APP_ICONS.find(icon => (
        icon.sku === sku
        && (!icon.specialization || icon.specialization === specialization)
    ))
);

const resolveHomeAppIcon = (homeApp: AvailableApp, allSectors: Sector[]) => {
    const app = homeApp.app;

    if (!app) return EventOutlinedIcon;

    const sector = allSectors.find(sector => sector.id === homeApp.orgApp.configuration?.sectorId);

    return getSectorIcon(sector)
        ?? findHomeAppIcon(app.sku, app.specialization)?.icon
        ?? EventOutlinedIcon;
};

const MyApps = () => {
    const {t, i18n} = useTranslation();
    const {availableApps} = useMe();
    const allSectors = useAllSectors();
    const visibleApps = availableApps.filter(({app}) => app?.category === AppCategory.APP);
    const {lockedApps} = useCrossSaleOpportunities();

    return (
        <Stack gap={2}>
            <Typography variant="h6" fontWeight={600}>
                {t("My Apps")}
            </Typography>

            <Box
                sx={{
                    display: "grid",
                    gridTemplateColumns: {
                        xs: "1fr",
                        sm: "repeat(2, minmax(0, 1fr))",
                    },
                    rowGap: 1.5,
                    columnGap: 2,
                }}
            >
                {visibleApps.map(homeApp => {
                    const route = buildAppLink(homeApp.app!, homeApp.orgApp);
                    const AppIcon = resolveHomeAppIcon(homeApp, allSectors);

                    return (
                        <Card
                            key={homeApp.orgApp.id}
                            component={RouterLink}
                            to={route}
                            variant="outlined"
                            sx={{
                                width: "100%",
                                maxWidth: 600,
                                cursor: "pointer",
                                color: "inherit",
                                textDecoration: "none",
                                borderColor: "divider",
                                borderRadius: 3,
                                boxShadow: "none",
                                "&:hover": {
                                    bgcolor: "action.hover",
                                },
                            }}
                        >
                            <Stack
                                direction={{xs: "column", md: "row"}}
                                alignItems="flex-start"
                                spacing={{xs: 1, md: 1.5}}
                                sx={{p: 1.5}}
                            >
                                <Box
                                    aria-hidden="true"
                                    sx={{
                                        width: {xs: 28, md: 40},
                                        height: {xs: 28, md: 40},
                                        color: CommonThemeColors.sys.primary.main,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        flexShrink: 0,
                                        mt: {xs: 0, md: 0.25},
                                    }}
                                >
                                    <AppIcon
                                        sx={theme => ({
                                            [theme.breakpoints.down("md")]: {
                                                fontSize: 20,
                                            },
                                        })}
                                    />
                                </Box>

                                <Stack gap={0.5} sx={{minWidth: 0, pt: {xs: 0, md: 0.25}}}>
                                    <Typography variant="body1">
                                        {homeApp.orgApp.name}
                                    </Typography>

                                    <Typography
                                        variant="body2"
                                        color={CommonThemeColors.sys.text.secondary}
                                    >
                                        {homeApp.app?.descriptionLocalized?.[i18n.language]
                                            || homeApp.app?.descriptionLocalized?.en}
                                    </Typography>
                                </Stack>
                            </Stack>
                        </Card>
                    );
                })}

                {/* Cross-selling: apps the organization is not subscribed to open a blurred preview */}
                {lockedApps.map(locked => {
                    const AppIcon = findHomeAppIcon(locked.opportunity.sku, locked.opportunity.specialization)?.icon
                        ?? EventOutlinedIcon;

                    return (
                        <Card
                            key={locked.opportunity.key}
                            component={RouterLink}
                            to={locked.link}
                            variant="outlined"
                            aria-label={`${locked.name} · ${t("Preview")}`}
                            sx={{
                                width: "100%",
                                maxWidth: 600,
                                cursor: "pointer",
                                color: "inherit",
                                textDecoration: "none",
                                borderColor: "divider",
                                borderStyle: "dashed",
                                borderRadius: 3,
                                boxShadow: "none",
                                "&:hover": {
                                    bgcolor: "action.hover",
                                },
                            }}
                        >
                            <Stack
                                direction={{xs: "column", md: "row"}}
                                alignItems="flex-start"
                                spacing={{xs: 1, md: 1.5}}
                                sx={{p: 1.5}}
                            >
                                <Box
                                    aria-hidden="true"
                                    sx={{
                                        width: {xs: 28, md: 40},
                                        height: {xs: 28, md: 40},
                                        color: CommonThemeColors.sys.text.secondary,
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        flexShrink: 0,
                                        mt: {xs: 0, md: 0.25},
                                    }}
                                >
                                    <AppIcon
                                        sx={theme => ({
                                            [theme.breakpoints.down("md")]: {
                                                fontSize: 20,
                                            },
                                        })}
                                    />
                                </Box>

                                <Stack gap={0.5} sx={{minWidth: 0, pt: {xs: 0, md: 0.25}}}>
                                    <Stack direction="row" alignItems="center" gap={1} flexWrap="wrap">
                                        <Typography variant="body1" color={CommonThemeColors.sys.text.secondary}>
                                            {locked.name}
                                        </Typography>
                                        <Chip
                                            size="small"
                                            icon={<LockOutlinedIcon/>}
                                            label={t("Preview")}
                                        />
                                    </Stack>

                                    <Typography
                                        variant="body2"
                                        color={CommonThemeColors.sys.text.secondary}
                                    >
                                        {locked.app?.descriptionLocalized?.[i18n.language]
                                            || locked.app?.descriptionLocalized?.en}
                                    </Typography>
                                </Stack>
                            </Stack>
                        </Card>
                    );
                })}
            </Box>
        </Stack>
    );
};

export default MyApps;
